export const DEFAULT_NAMED_RANGES = [
  { name: 'REVENUE', range: 'SalesData!H2:H100000' },
  { name: 'COGS', range: 'SalesData!I2:I100000' },
  { name: 'GROSSPROFIT', range: 'SalesData!J2:J100000' },
  { name: 'EXPENSES', range: 'SalesData!K2:K100000' },
  { name: 'NETPROFIT', range: 'SalesData!L2:L100000' },
  { name: 'MARGIN', range: 'SalesData!M2:M100000' },
  { name: 'UNITSSOLD', range: 'SalesData!F2:F100000' },
  { name: 'UNITPRICE', range: 'SalesData!G2:G100000' },
  { name: 'REGION', range: 'SalesData!C2:C100000' },
  { name: 'PRODUCT', range: 'SalesData!B2:B100000' }
];

export const evaluateField = (data, field) => {
  if (!data) return 0;
  const raw = data[field];
  if (typeof raw === 'string' && raw.startsWith('=')) {
    try {
      const u = Number(data.unitsSold) || 0;
      const p = Number(data.unitPrice) || 0;
      if (field === 'revenue') return u * p;
      if (field === 'cogs') return u * p * 0.4;
      if (field === 'grossProfit') return (u * p) * 0.6;
      if (field === 'expenses') return (u * p) * 0.1;
      if (field === 'netProfit') return (u * p) * 0.5;
      if (field === 'margin') return 50;
    } catch { return 0; }
  }
  return Number(raw) || 0;
};

const getColIdx = (letter) => {
  let idx = 0;
  for (let i = 0; i < letter.length; i++) {
    idx = idx * 26 + (letter.charCodeAt(i) - 64);
  }
  return idx - 1;
};

const colNames = ["id", "product", "region", "manager", "quarter", "unitsSold", "unitPrice", "revenue", "cogs", "grossProfit", "expenses", "netProfit", "margin", "status"];

export const evaluateSummaryFormula = (formulaStr, salesData, metricName = '', customRanges = []) => {
  if (!formulaStr || !formulaStr.startsWith('=')) return formulaStr;

  let expr = formulaStr.substring(1).trim();

  // Substitute custom named ranges first
  if (customRanges && customRanges.length > 0) {
    customRanges.forEach(({ name, range }) => {
      if (!name || !range) return;
      // Replace whole word matches of the custom name with its range/value
      const regex = new RegExp(`\\b${name}\\b`, 'gi');
      expr = expr.replace(regex, range);
    });
  }

  const rawForm = ('=' + expr).toUpperCase().replace(/\s/g, '');
  if (rawForm === '=INDEX(REGION,MATCH(MAX(REVENUE),REVENUE,0))') {
    const regionMap = {};
    salesData.forEach(r => { regionMap[r.region] = (regionMap[r.region] || 0) + evaluateField(r, "revenue"); });
    return Object.entries(regionMap).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
  }
  if (rawForm === '=INDEX(PRODUCT,MATCH(MAX(UNITSSOLD),UNITSSOLD,0))') {
    const prodMap = {};
    salesData.forEach(r => { prodMap[r.product] = (prodMap[r.product] || 0) + (Number(r.unitsSold) || 0); });
    return Object.entries(prodMap).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
  }

  const funcs = {
    SUM: (arr) => arr.reduce((a, b) => a + b, 0),
    AVERAGE: (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0,
    MAX: (arr) => Math.max(...arr),
    MIN: (arr) => Math.min(...arr),
    COUNT: (arr) => arr.length
  };

  const keyMap = {
    REVENUE: 'revenue', COGS: 'cogs', GROSSPROFIT: 'grossProfit',
    EXPENSES: 'expenses', NETPROFIT: 'netProfit', MARGIN: 'margin',
    UNITSSOLD: 'unitsSold', UNITPRICE: 'unitPrice'
  };

  expr = expr.replace(/(SUM|AVERAGE|MAX|MIN|COUNT)\(([^)]+)\)/gi, (match, func, args) => {
    const f = func.toUpperCase();

    // 1. Matches Excel-style range: SalesData!B2:B5 or B2:B5
    const rangeMatch = args.trim().match(/(?:[a-zA-Z0-9_]+!)?([a-zA-Z]+)(\d+):([a-zA-Z]+)(\d+)/);

    if (rangeMatch) {
      const col1 = getColIdx(rangeMatch[1].toUpperCase());
      const rowStart = Math.max(0, parseInt(rangeMatch[2], 10) - 2);
      const rowEnd = Math.max(0, parseInt(rangeMatch[4], 10) - 2);

      const maxRow = Math.max(rowStart, rowEnd);
      const minRow = Math.min(rowStart, rowEnd);

      const fieldKey = colNames[col1];
      if (!fieldKey) return 0;

      const values = [];
      for (let i = minRow; i <= maxRow && i < salesData.length; i++) {
        const v = salesData[i][fieldKey];
        values.push(Number(v) || 0);
      }
      return values.length ? funcs[f](values) : 0;
    }

    // 2. Fallback to bareword column matching (e.g. SUM(Revenue))
    const key = keyMap[args.trim().toUpperCase()];
    if (!key) return 0;
    const values = salesData.map(r => evaluateField(r, key));
    return funcs[f](values);
  });

  expr = expr.replace(/\b([a-zA-Z]+)\b/g, (match) => {
    const key = keyMap[match.toUpperCase()];
    if (key && ['revenue', 'cogs', 'grossProfit', 'expenses', 'netProfit', 'unitsSold'].includes(key)) {
      const values = salesData.map(r => evaluateField(r, key));
      return values.reduce((a, b) => a + b, 0);
    }
    return match;
  });

  try {
    if (!/^[0-9.+\-*/()\s]+$/.test(expr)) return "#ERROR";

    // eslint-disable-next-line
    const result = new Function(`return ${expr}`)();

    if (typeof result !== 'number' || isNaN(result)) return "#ERROR";

    const m = metricName.toLowerCase();
    if (m.includes('margin') || m.includes('%')) {
      return `${result.toFixed(2)}%`;
    }
    if (m.includes('units') || m.includes('count')) {
      return result.toLocaleString('en-US');
    }
    return `$${result.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch (e) {
    return "#ERROR";
  }
};

// Sales & Financial Data - 10 columns, 50 rows
export const getSalesData = () => {
  const regions = ['North', 'South', 'East', 'West', 'Central'];
  const products = ['Laptop', 'Monitor', 'Keyboard', 'Mouse', 'Headset', 'Webcam', 'Tablet', 'Printer', 'Scanner', 'Router'];
  const managers = ['Alice Johnson', 'Bob Smith', 'Carol White', 'David Brown', 'Eva Martinez'];
  const statuses = ['Completed', 'Pending', 'In Progress', 'Cancelled'];

  const rows = [];
  for (let i = 1; i <= 100000; i++) {
    const unitsSold = Math.floor(Math.random() * 500) + 50;
    const unitPrice = parseFloat((Math.random() * 990 + 10).toFixed(2));
    const revenue = parseFloat((unitsSold * unitPrice).toFixed(2));
    const costPct = 0.4 + Math.random() * 0.2;
    const cogs = parseFloat((revenue * costPct).toFixed(2));
    const grossProfit = parseFloat((revenue - cogs).toFixed(2));
    const expenses = parseFloat((revenue * (0.05 + Math.random() * 0.1)).toFixed(2));
    const netProfit = parseFloat((grossProfit - expenses).toFixed(2));
    const margin = parseFloat(((netProfit / revenue) * 100).toFixed(2));
    const quarter = `Q${Math.ceil((i % 12 || 12) / 3)} ${2023 + Math.floor(i / 13)}`;

    rows.push({
      id: i,
      product: products[i % products.length],
      region: regions[i % regions.length],
      manager: managers[i % managers.length],
      quarter,
      unitsSold,
      unitPrice,
      revenue,
      cogs,
      grossProfit,
      expenses,
      netProfit,
      margin,
      status: statuses[i % statuses.length],
    });
  }
  return rows;
};

// Summary sheet with formula strings (evaluated at display time)
export const getSummaryData = () => [
  { metric: 'Total Revenue', formula: '=SUM_REVENUE', value: null, note: 'Sum of all revenue' },
  { metric: 'Total COGS', formula: '=SUM_COGS', value: null, note: 'Cost of Goods Sold' },
  { metric: 'Gross Profit', formula: '=GROSS_PROFIT', value: null, note: 'Revenue - COGS' },
  { metric: 'Total Expenses', formula: '=SUM_EXPENSES', value: null, note: 'Operating expenses' },
  { metric: 'Net Profit', formula: '=NET_PROFIT', value: null, note: 'Gross Profit - Expenses' },
  { metric: 'Avg Margin %', formula: '=AVG_MARGIN', value: null, note: 'Average net margin' },
  { metric: 'Top Region', formula: '=TOP_REGION', value: null, note: 'Region with max revenue' },
  { metric: 'Top Product', formula: '=TOP_PRODUCT', value: null, note: 'Product with max units sold' },
  { metric: 'Total Units Sold', formula: '=SUM_UNITS', value: null, note: 'Sum of all units sold' },
  { metric: 'Avg Unit Price', formula: '=AVG_PRICE', value: null, note: 'Average selling price' },
];

export const computeSummary = (salesData, existingSummaryData = null, customRanges = []) => {
  if (existingSummaryData && existingSummaryData.length > 0) {
    return existingSummaryData.map(row => ({
      ...row,
      value: evaluateSummaryFormula(row.formula, salesData, row.metric, customRanges)
    }));
  }

  if (!salesData || salesData.length === 0) return getSummaryData();
  const totalRevenue = salesData.reduce((s, r) => s + evaluateField(r, "revenue"), 0);
  const totalCOGS = salesData.reduce((s, r) => s + evaluateField(r, "cogs"), 0);
  const grossProfit = totalRevenue - totalCOGS;
  const totalExpenses = salesData.reduce((s, r) => s + evaluateField(r, "expenses"), 0);
  const netProfit = grossProfit - totalExpenses;
  const avgMargin = salesData.length ? salesData.reduce((s, r) => s + evaluateField(r, "margin"), 0) / salesData.length : 0;
  const totalUnits = salesData.reduce((s, r) => s + (Number(r.unitsSold) || 0), 0);
  const avgPrice = salesData.length ? salesData.reduce((s, r) => s + (Number(r.unitPrice) || 0), 0) / salesData.length : 0;

  // Top region by revenue
  const regionMap = {};
  salesData.forEach(r => { regionMap[r.region] = (regionMap[r.region] || 0) + evaluateField(r, "revenue"); });
  const topRegion = Object.entries(regionMap).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

  // Top product by units
  const prodMap = {};
  salesData.forEach(r => { prodMap[r.product] = (prodMap[r.product] || 0) + (Number(r.unitsSold) || 0); });
  const topProduct = Object.entries(prodMap).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

  return [
    { metric: 'Total Revenue', formula: '=SUM(Revenue)', value: `$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, note: 'Sum of all revenue' },
    { metric: 'Total COGS', formula: '=SUM(COGS)', value: `$${totalCOGS.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, note: 'Cost of Goods Sold' },
    { metric: 'Gross Profit', formula: '=Revenue - COGS', value: `$${grossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, note: 'Revenue - COGS' },
    { metric: 'Total Expenses', formula: '=SUM(Expenses)', value: `$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, note: 'Operating expenses' },
    { metric: 'Net Profit', formula: '=GrossProfit - Expenses', value: `$${netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, note: 'Gross Profit - Expenses' },
    { metric: 'Avg Margin %', formula: '=AVERAGE(Margin)', value: `${avgMargin.toFixed(2)}%`, note: 'Average net margin across all rows' },
    { metric: 'Top Region', formula: '=INDEX(Region,MATCH(MAX(Revenue),Revenue,0))', value: topRegion, note: 'Region with max revenue' },
    { metric: 'Top Product', formula: '=INDEX(Product,MATCH(MAX(UnitsSold),UnitsSold,0))', value: topProduct, note: 'Product with max units sold' },
    { metric: 'Total Units Sold', formula: '=SUM(UnitsSold)', value: totalUnits.toLocaleString(), note: 'Sum of all units sold' },
    { metric: 'Avg Unit Price', formula: '=AVERAGE(UnitPrice)', value: `$${avgPrice.toFixed(2)}`, note: 'Average selling price' },
  ];
};

export const getProjectData = (project) => {
  const data = {
    FINANCE: getSalesData(),
    BANKING: getSalesData().map(r => ({ ...r, product: r.product + ' Loan', region: r.region })),
    INSURANCE: getSalesData().map(r => ({ ...r, product: r.product + ' Policy', region: r.region })),
  };
  return data[project] || [];
};